import moment from 'moment';
import { getItemProp } from '../../../utils';
import { FeatureTypeTagVariants } from '../../../utils/const';
import type { TagVariant } from '../../../components/tag';
import type { ReorderCardData } from '../../../components/reorder-card-item/types';
import type {
    SectionPlacementEventItem,
    SectionPlacementFeaturedItem,
    SectionPlacementGameItem,
    SectionPlacementPromotionItem,
} from '../../../stores/section-placements';

type TranslationLike = {
    language?: 'en' | 'fr';
    lang?: 'en' | 'fr';
    title?: string | null;
    subTitle?: string | null;
    subtitle?: string | null;
    image?: string | null;
    thumbnail?: string | null;
};

type StatusInput = {
    startDate?: string | null;
    endDate?: string | null;
    isActive?: boolean | null;
};

type PromotionCardOptions = {
    showTypeTag?: boolean;
};

export function formatDateRange(
    startDate?: string | null,
    endDate?: string | null
) {
    if (!startDate || !endDate) return '--';
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid()) return '--';
    return `${start.format('MM-YYYY')} to ${end.format('MM-YYYY')}`;
}

export function getTranslation<T extends TranslationLike>(
    items: T[] | undefined,
    lang: 'en' | 'fr'
) {
    if (!items) return undefined;
    return items.find((item) => item.language === lang || item.lang === lang);
}

function getStatusFromDates({
    startDate,
    endDate,
    isActive,
}: StatusInput): { statusText: string; statusVariant: TagVariant } {
    const isUpcoming = startDate ? moment().isBefore(moment(startDate)) : false;
    const isExpired = endDate ? moment().isAfter(moment(endDate)) : false;

    if (isExpired) {
        return { statusText: 'Expired', statusVariant: 'error' };
    }

    if (isUpcoming) {
        return { statusText: 'Upcoming', statusVariant: 'blue' };
    }

    if (isActive) {
        return { statusText: 'Active', statusVariant: 'success' };
    }

    return { statusText: 'Inactive', statusVariant: 'grey' };
}

export function toFeaturedCardData(
    featured: SectionPlacementFeaturedItem
): ReorderCardData {
    const title =
        getItemProp(featured.items, 'en', 'title') ||
        getItemProp(featured.items, 'en', 'textTitle') ||
        '--';
    const subtitle =
        getItemProp(featured.items, 'fr', 'title') ||
        getItemProp(featured.items, 'fr', 'textTitle') ||
        null;
    const imageUrl = getItemProp(featured.items, 'en', 'backgroundImage') || '';
    const { statusText, statusVariant } = getStatusFromDates({
        startDate: featured.startDate,
        endDate: featured.endDate,
        isActive: featured.isActive,
    });
    const tagVariant =
        FeatureTypeTagVariants[
            featured.type as keyof typeof FeatureTypeTagVariants
        ] || 'grey';

    return {
        title,
        subtitle,
        imageUrl,
        statusText,
        statusVariant,
        meta: formatDateRange(featured.startDate, featured.endDate),
        tags: [{ label: featured.type, variant: tagVariant }],
    };
}

export function toPromotionCardData(
    promotion: SectionPlacementPromotionItem,
    options: PromotionCardOptions = {}
): ReorderCardData {
    const title = getItemProp(promotion.items, 'en', 'title') || '--';
    const subtitle = getItemProp(promotion.items, 'en', 'subTitle') || null;
    const imageUrl =
        getItemProp(promotion.items, 'en', 'thumbnail') ||
        getItemProp(promotion.items, 'en', 'background') ||
        '';
    const { statusText, statusVariant } = getStatusFromDates({
        startDate: promotion.startDate,
        endDate: promotion.endDate,
        isActive: promotion.isActive,
    });
    const typeTag =
        promotion.type === 'coupon'
            ? { label: 'Coupon', variant: 'magenta' as const }
            : promotion.type === 'in_store'
            ? { label: 'In-store', variant: 'volcano' as const }
            : null;

    return {
        title,
        subtitle,
        imageUrl,
        statusText,
        statusVariant,
        meta: formatDateRange(promotion.startDate, promotion.endDate),
        tags: options.showTypeTag && typeTag ? [typeTag] : undefined,
    };
}

export function toEventCardData(
    event: SectionPlacementEventItem
): ReorderCardData {
    const translation = getTranslation(event.translations, 'en');
    const title = translation?.title || '--';
    const subtitle = translation?.subTitle || translation?.subtitle || null;
    const imageUrl = translation?.image || '';
    const { statusText, statusVariant } = getStatusFromDates({
        startDate: event.startDate,
        endDate: event.endDate,
        isActive: event.isActive,
    });

    return {
        title,
        subtitle,
        imageUrl,
        statusText,
        statusVariant,
        meta: formatDateRange(event.startDate, event.endDate),
    };
}

export function toGameCardData(
    game: SectionPlacementGameItem
): ReorderCardData {
    const translation = getTranslation(game.translations, 'en');
    const title =
        translation?.title || game.title?.en || translation?.subtitle || '--';
    const subtitle =
        translation?.subTitle || translation?.subtitle || game.subTitle?.en;
    const imageUrl = translation?.image || game.image?.en || '';
    const isActive =
        typeof game.isActive === 'boolean'
            ? game.isActive
            : game.status === 'active';
    const { statusText, statusVariant } = getStatusFromDates({
        startDate: game.startDate,
        endDate: game.endDate,
        isActive,
    });

    return {
        title,
        subtitle,
        imageUrl,
        statusText,
        statusVariant,
        meta: formatDateRange(game.startDate, game.endDate),
    };
}
